import React, {useState} from "react"
import Grid from "@mui/material/Grid";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import {Maybe} from "graphql/jsutils/Maybe";
import CCOAccordion from "../../fundamentals/CCOAccordion";
import CCOLink from "../../fundamentals/CCOLink";
import C039Author from "../../content/C039Author/C039Author";
import {RichTextElement} from "../../fundamentals/CCORichText/CCORichTextHelper";
import {SosAuthorType, SosPublicationSeriesItemType, SosPublicationType} from "../../types";


export interface Props {
    sosPublication: SosPublicationType
}


function P011PublicationAccordion(props: Props) {
    const accordions = getAccordions(props.sosPublication);
    const [expandedAccordions, setExpandedAccordions] = useState(
        [accordions.length > 0 ? accordions[0].id : '']);
    const handleAccordionChange = (accordionId: string) => (_event: React.SyntheticEvent,
                                                            isExpanded: boolean) => {
        isExpanded ? setExpandedAccordions([...expandedAccordions, accordionId]) :
            setExpandedAccordions(expandedAccordions.filter(id => id !== accordionId));
    };
    return <React.Fragment key='accordions'>
        {accordions.length > 0 &&
            accordions.map((accordion, index) => {
                return <CCOAccordion key={'accordion_' + index} id={accordion.id}
                                     title={accordion.title}
                                     initiallyExpanded={index === 0}
                                     expanded={expandedAccordions.includes(accordion.id)}
                                     onChange={handleAccordionChange(accordion.id)}>
                    {accordion.content()}
                </CCOAccordion>
            })}
    </React.Fragment>
}

function getAccordions(sosPublication: SosPublicationType) {
    const details = getAccordionDetails(sosPublication);
    const directory = getAccordionDirectory(sosPublication);
    const authors = getAccordionAuthors(sosPublication);
    const moreOfSeries = getAccordionMoreOfSeries(sosPublication);

    const accordions = [];
    details && accordions.push(details);
    directory && accordions.push(directory);
    authors && accordions.push(authors);
    moreOfSeries && accordions.push(moreOfSeries);
    return accordions;
}

function getAccordionDetails(sosPublication: SosPublicationType) {
    return sosPublication.details && {
        id: sosPublication.id,
        title: 'Beschreibung',
        content: () => getAccordionDetailsContent(sosPublication)
    }
}

function getAccordionDetailsContent(sosPublication: SosPublicationType) {
    return <CCORichText metadataId={sosPublication.id} key={'details'}
                        propertyName='properties.details'
                        textAsTree={sosPublication.details!.textAsTree as RichTextElement}/>
}

function getAccordionDirectory(sosPublication: SosPublicationType) {
    return sosPublication.directory && {
        id: sosPublication.id + '_inhalt',
        title: 'Inhaltsverzeichnis',
        content: () => getAccordionDirectoryContent(sosPublication)
    }
}

function getAccordionDirectoryContent(sosPublication: SosPublicationType) {
    return <CCORichText metadataId={sosPublication.id} key={'directory'}
                        propertyName='properties.directory'
                        textAsTree={sosPublication.directory!.textAsTree as RichTextElement}/>
}

function getAccordionAuthors(sosPublication: SosPublicationType) {
    return sosPublication.vitae?.length && sosPublication.vitae?.length > 0 &&
        {
            id: sosPublication.id + '_autor',
            title: 'Autor*innen',
            content: () => getAccordionAuthorsContent(sosPublication)
        }
}

function getAccordionAuthorsContent(sosPublication: SosPublicationType) {
    return <React.Fragment key='authors'>
        {sosPublication.vitae?.map((author: Maybe<SosAuthorType>, index) => {
            return <React.Fragment key={'author_' + index}>
                <CCOHeadline metadataId={sosPublication.id} type='h6' justify='left'
                             headline={(author?.authortitle ? author?.authortitle + ' ' : '') + author?.name + ' ' + author?.surname}/>
                <CCORichText textAsTree={author?.teaserText!.textAsTree as RichTextElement}/>
                <Grid sx={{my: 3}}></Grid>
            </React.Fragment>
        })}
    </React.Fragment>
}

function getAccordionMoreOfSeries(sosPublication: SosPublicationType) {
    return sosPublication.moreofseries?.length && sosPublication.moreofseries?.length > 0 &&
        {
            id: sosPublication.id + '_mehr',
            title: 'Mehr aus dieser Reihe',
            content: () => getAccordionMoreOfSeriesContent(sosPublication)
        }
}

function getAccordionMoreOfSeriesContent(sosPublication: SosPublicationType) {
    return <React.Fragment key='more'>
        {sosPublication.moreofseries?.map((item: SosPublicationSeriesItemType | null, index) => {
            return <Grid key={'more_' + index} sx={{'a': {color: 'primary.main'}}}>
                <CCOLink target={item!} contentId={item?.id} name={item?.title as any}/>
                <C039Author publication={item!}/>
                <br/>
                {item?.extDisplayedDate && item?.extDisplayedDate?.substr(0, 4)}
                <br/>
                <Grid sx={{my: 3}}></Grid>
            </Grid>
        })}
    </React.Fragment>
}

export default P011PublicationAccordion
